import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navItems = [
  { to: "/", label: "首页", match: (p: string) => p === "/" },
  { to: "/archives", label: "悬疑档案", match: (p: string) => p.startsWith("/archives") || p.startsWith("/article") },
  { to: "/about", label: "关于我们", match: (p: string) => p.startsWith("/about") },
  { to: "/faq", label: "常见问题", match: (p: string) => p.startsWith("/faq") },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/archives?q=${encodeURIComponent(value)}` : "/archives");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-800 bg-background-950/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 w-full max-w-content items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer" aria-label="猎奇吃瓜 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md border border-primary-500/50 bg-primary-500/10 text-primary-400">
            <i className="ri-eye-2-line text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-wide text-foreground-50">猎奇吃瓜</span>
            <span className="mt-0.5 hidden text-[10px] tracking-[0.32em] text-foreground-500 sm:block">ODDITY ARCHIVES</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          {navItems.map((item) => {
            const active = item.match(location.pathname);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`whitespace-nowrap rounded-md px-3.5 py-2 text-sm transition-colors ${
                  active
                    ? "bg-background-800 text-primary-300"
                    : "text-foreground-300 hover:bg-background-800/70 hover:text-primary-300"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden items-center lg:flex" role="search">
            <div className="relative">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500" />
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索档案关键词"
                aria-label="搜索档案关键词"
                className="w-48 rounded-full border border-background-700 bg-background-900 py-2 pl-9 pr-3 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500/60"
              />
            </div>
          </form>

          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={mobileOpen}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-700 text-foreground-200 transition-colors hover:border-primary-500/60 hover:text-primary-300 md:hidden"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      <div className="hidden border-t border-background-800/70 md:block">
        <div className="mx-auto flex w-full max-w-content items-center gap-1 overflow-x-auto px-4 py-1.5 md:px-6">
          <span className="mr-1 flex items-center gap-1 whitespace-nowrap text-xs text-foreground-500">
            <i className="ri-folder-open-line" /> 分类
          </span>
          {categories.map((category) => (
            <Link
              key={category.slug}
              to={`/archives?category=${category.slug}`}
              className="whitespace-nowrap rounded-full px-3 py-1 text-xs text-foreground-400 transition-colors hover:bg-background-800 hover:text-primary-300"
            >
              {category.name}
            </Link>
          ))}
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-800 bg-background-950 md:hidden">
          <div className="mx-auto w-full max-w-content px-4 py-4">
            <form onSubmit={submitSearch} className="mb-4" role="search">
              <div className="relative">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500" />
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索档案关键词"
                  aria-label="搜索档案关键词"
                  className="w-full rounded-md border border-background-700 bg-background-900 py-2.5 pl-9 pr-3 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none"
                />
              </div>
            </form>
            <nav className="flex flex-col" aria-label="移动端导航">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center justify-between rounded-md px-3 py-3 text-sm ${
                    item.match(location.pathname)
                      ? "bg-background-800 text-primary-300"
                      : "text-foreground-300"
                  }`}
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-500" />
                </Link>
              ))}
            </nav>
            <div className="mt-4 flex flex-wrap gap-2 border-t border-background-800 pt-4">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/archives?category=${category.slug}`}
                  className="rounded-full border border-background-700 px-3 py-1 text-xs text-foreground-400"
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}